import { WRF_PERCENTILE_STATUSES } from "@bindery/shared/contracts/orientation";
import { LNF_FORM_LENGTH } from "@bindery/shared/contracts/study";
import * as z from "zod";
import { ORIENTATION_API_PREFIX } from "./errors";

export const LNF_FORM_PATH = `${ORIENTATION_API_PREFIX}/dibels/lnf-form`;

const ISO_CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

// The period follows the aide's local date, not the Worker's UTC day.
export const lnfFormRequestSchema = z.strictObject({
  grade: z.enum(["K", "1"]),
  localDate: z.string().regex(ISO_CALENDAR_DATE),
});
export type LnfFormRequest = z.infer<typeof lnfFormRequestSchema>;

export const lnfFormResponseSchema = z.object({
  /** `<grade>.<period>.<year>`, as `buildLnfFormId` writes it. */
  formId: z.string().min(1).max(64),
  /** In sequence order, as the child reads them. */
  letters: z.array(z.string().min(1).max(4)).min(1).max(LNF_FORM_LENGTH),
});
export type LnfFormResponse = z.infer<typeof lnfFormResponseSchema>;

const GRADE_PERIOD = /^\d+\.\d+$/;
const MAX_RAW_SCORE = 500;

const gradePeriodSchema = z.string().regex(GRADE_PERIOD);
const normsYearSchema = z.coerce.number().int().min(2000).max(2100).optional();
const rawScoreSchema = z.coerce.number().int().min(0).max(MAX_RAW_SCORE);

export const WRF_SEQUENCE_PATH = `${ORIENTATION_API_PREFIX}/dibels/wrf-sequence`;
export const WRF_PERCENTILE_PATH = `${ORIENTATION_API_PREFIX}/dibels/wrf-percentile`;
export const INSTRUCTIONAL_GRADE_PATH = `${ORIENTATION_API_PREFIX}/dibels/instructional-grade`;
export const ORF_PASSAGE_PATH = `${ORIENTATION_API_PREFIX}/dibels/orf-passage`;
export const ORF_PERCENTILES_PATH = `${ORIENTATION_API_PREFIX}/dibels/orf-percentiles`;

export const DIBELS_PATHS = [
  LNF_FORM_PATH,
  WRF_SEQUENCE_PATH,
  WRF_PERCENTILE_PATH,
  INSTRUCTIONAL_GRADE_PATH,
  ORF_PASSAGE_PATH,
  ORF_PERCENTILES_PATH,
] as const;

export const wrfSequenceRequestSchema = z.strictObject({
  grade: z.enum(["K", "0", "1", "2", "3"]),
  localDate: z.string().regex(ISO_CALENDAR_DATE),
});
export type WrfSequenceRequest = z.infer<typeof wrfSequenceRequestSchema>;

export const wrfSequenceResponseSchema = z.object({
  /** In sequence order, as the child reads them. */
  words: z.array(z.object({ word: z.string(), sequence: z.number().int() })),
});
export type WrfSequenceResponse = z.infer<typeof wrfSequenceResponseSchema>;

/** `year` defaults to `WRF_NORMS_YEAR` on the server. */
export const wrfPercentileRequestSchema = z.strictObject({
  gradePeriod: gradePeriodSchema,
  rawScore: rawScoreSchema,
  year: normsYearSchema,
});
export type WrfPercentileRequest = z.input<typeof wrfPercentileRequestSchema>;

export const wrfPercentileResponseSchema = z.object({
  status: z.enum(WRF_PERCENTILE_STATUSES),
  percentile: z.number().nullable(),
  bound: z.number().optional(),
});
export type WrfPercentileResponse = z.infer<typeof wrfPercentileResponseSchema>;

export const instructionalGradeRequestSchema = z.strictObject({
  wrfScore: rawScoreSchema,
  year: normsYearSchema,
});
export type InstructionalGradeRequest = z.input<
  typeof instructionalGradeRequestSchema
>;

export const instructionalGradeResponseSchema = z.object({
  gradePeriod: z.string(),
  percentile: z.number(),
  found: z.boolean(),
});
export type InstructionalGradeResponse = z.infer<
  typeof instructionalGradeResponseSchema
>;

export const orfPassageRequestSchema = z.strictObject({
  gradePeriod: gradePeriodSchema,
});
export type OrfPassageRequest = z.infer<typeof orfPassageRequestSchema>;

/** `passage` is null when the grade period has no progress-monitoring passage. */
export const orfPassageResponseSchema = z.object({
  passage: z
    .object({
      gradePeriod: z.string(),
      title: z.string(),
      text: z.string(),
      words: z.array(z.string()),
    })
    .nullable(),
});
export type OrfPassageResponse = z.infer<typeof orfPassageResponseSchema>;

export const orfPercentilesRequestSchema = z.strictObject({
  gradePeriod: gradePeriodSchema,
  wordsCorrect: rawScoreSchema,
  accuracyPercent: z.coerce.number().int().min(0).max(100),
  year: normsYearSchema,
});
export type OrfPercentilesRequest = z.input<typeof orfPercentilesRequestSchema>;

const orfLookupSchema = z.object({
  percentile: z.number(),
  found: z.boolean(),
});
export const orfPercentilesResponseSchema = z.object({
  wordsCorrect: orfLookupSchema,
  accuracy: orfLookupSchema,
});
export type OrfPercentilesResponse = z.infer<
  typeof orfPercentilesResponseSchema
>;
